/**
 * Single source of truth for every ribbon keyboard shortcut (proposal §7).
 *
 * `useRibbonShortcuts.ts` binds each entry's `hotkey` via `useHotkeys`, the
 * ribbon button components append each entry's `display` to their own
 * tooltip, and the cheatsheet modal (`keyboard-shortcuts.tsx`) renders
 * `SHORTCUT_GROUPS` derived from this same list - so the bound key, the
 * tooltip hint, and the cheatsheet can never drift out of sync with each
 * other. Keep this in sync with docs/keyboard-shortcuts-proposal.md §7.
 */

export type ShortcutSection =
  | 'Measure'
  | 'Add a new Fitting'
  | 'Size'
  | 'Filters'
  | 'Panels';

type ShortcutEntry = {
  section: ShortcutSection;
  label: string;
  /**
   * exact combo passed to `useHotkeys` - `null` for the one entry (Esc)
   * that's handled by the legacy `useKeyEsc`/`DomEventExt` listeners
   * instead of `react-hotkeys-hook` (§2.1/§4), so it's documented here for
   * the cheatsheet but not bound by `useRibbonShortcuts.ts`.
   */
  hotkey: string | null;
  /** human-readable display, e.g. "R", "Shift + D" */
  display: string;
  /**
   * optional usage note shown in the cheatsheet when the shortcut has a
   * non-obvious precondition, e.g. needing a selection first
   */
  tip?: string;
};

export const RIBBON_SHORTCUTS = {
  ruler: { section: 'Measure', label: 'Ruler', hotkey: 'r', display: 'R' },
  magicBrush: {
    section: 'Measure',
    label: 'System Correction',
    hotkey: 'b',
    display: 'B',
  },
  insulationPainting: {
    section: 'Measure',
    label: 'Insulation Painting',
    hotkey: 'w',
    display: 'W',
  },
  pipeSnip: {
    section: 'Measure',
    label: 'Pipe Snipping Tool',
    hotkey: 'c',
    display: 'C',
  },
  insulationPolygon: {
    section: 'Measure',
    label: 'Insulation Polygon Tool',
    hotkey: 'g',
    display: 'G',
  },
  clearSelection: {
    section: 'Measure',
    label: 'Clear Selection & Stop Tool',
    hotkey: null,
    display: 'Esc',
  },
  duct: {
    section: 'Add a new Fitting',
    label: 'Duct',
    hotkey: 'd',
    display: 'D',
  },
  pipe: {
    section: 'Add a new Fitting',
    label: 'Pipe',
    hotkey: 'p',
    display: 'P',
  },
  plumbing: {
    section: 'Add a new Fitting',
    label: 'Plumbing',
    hotkey: 'u',
    display: 'U',
  },
  ductLength: {
    section: 'Add a new Fitting',
    label: 'Duct Length',
    hotkey: 'shift+d',
    display: 'Shift + D',
  },
  pipeLength: {
    section: 'Add a new Fitting',
    label: 'Pipe Length',
    hotkey: 'shift+p',
    display: 'Shift + P',
  },
  equipment: {
    section: 'Add a new Fitting',
    label: 'Add New Equipment',
    hotkey: 'e',
    display: 'E',
  },
  sizeMergeStamp: {
    section: 'Size',
    label: 'Size Merge Stamp',
    hotkey: 's',
    display: 'S',
    tip: 'Select a size first to activate the stamp tool',
  },
  lockEmptySize: {
    section: 'Size',
    label: 'Lock Empty (size)',
    hotkey: 'shift+l',
    display: 'Shift + L',
  },
  symbolFilter: {
    section: 'Filters',
    label: 'Symbol Type / Group Filter',
    hotkey: 'f',
    display: 'F',
  },
  systemFilter: {
    section: 'Filters',
    label: 'System Filter',
    hotkey: 'shift+f',
    display: 'Shift + F',
  },
  lockEmptySymbol: {
    section: 'Filters',
    label: 'Lock Empty (symbol)',
    hotkey: 'l',
    display: 'L',
  },
  inverseFiltering: {
    section: 'Filters',
    label: 'Inverse Filtering',
    hotkey: 'i',
    display: 'I',
  },
  takeoffSummary: {
    section: 'Panels',
    label: 'Takeoff Summary',
    hotkey: 't',
    display: 'T',
  },
  reviewFittings: {
    section: 'Panels',
    label: 'Review Fittings',
    hotkey: 'v',
    display: 'V',
  },
} as const satisfies Record<string, ShortcutEntry>;

export type RibbonShortcutId = keyof typeof RIBBON_SHORTCUTS;

/** the exact `useHotkeys` combo string for an entry, e.g. `'shift+d'` */
export const hotkeyFor = (id: RibbonShortcutId): string => {
  const hotkey = RIBBON_SHORTCUTS[id].hotkey;
  if (!hotkey) {
    throw new Error(
      `RIBBON_SHORTCUTS.${id} has no bindable hotkey (handled elsewhere)`,
    );
  }
  return hotkey;
};

/** " (R)" / " (Shift + D)" - ready to append straight onto a tooltip string */
export const hintFor = (id: RibbonShortcutId): string =>
  ` (${RIBBON_SHORTCUTS[id].display})`;
